<template>
  <div class="fixed z-20 inset-0 overflow-y-auto" aria-labelledby="delete-course-modal-title" role="dialog" aria-modal="true"
       @keydown.esc="close">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="close"></div>
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

      <form class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
            novalidate @submit.prevent="submit">
        <div class="sm:flex sm:items-start">
          <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="mt-3 sm:mt-0 sm:ml-4 w-full">
            <h3 id="delete-course-modal-title" class="text-lg leading-6 font-medium text-gray-900 text-center sm:text-left">
              {{ $t('projects.delete_course_title') }}
            </h3>
            <p class="mt-2 text-sm text-gray-500">{{ $t('projects.delete_course_text') }}</p>

            <div v-if="hasStudents" class="mt-3 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800" role="alert">
              {{ $t('projects.delete_course_has_students') }}
            </div>

            <div class="mt-4">
              <label for="delete-course-confirmation" class="block text-sm text-gray-700">
                {{ $t('projects.delete_course_confirmation_label') }} <strong class="font-semibold">{{ course.title }}</strong>
              </label>
              <input id="delete-course-confirmation" ref="confirmation" v-model="confirmation" type="text" autocomplete="off"
                     class="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm">
            </div>
          </div>
        </div>

        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button type="submit" :disabled="deleting || !isConfirmed"
                  class="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm">
            <font-awesome-icon v-if="deleting" class="mr-2 h-4 w-4 animate-spin" icon="spinner"/>
            {{ hasStudents ? $t('projects.delete_course_force') : $t('projects.delete_course') }}
          </button>
          <button type="button" :disabled="deleting" @click="close"
                  class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue sm:mt-0 sm:w-auto sm:text-sm">
            {{ $t('general.cancel') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import CourseService from "@/services/project/CourseService";
import NotificationService from "@/services/notification/NotificationService";

const HTTP_CONFLICT = 409

export default {
  name: "DeleteCourseModal",
  props: {
    course: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'deleted'],
  data() {
    return {
      confirmation: '',
      deleting: false,
      // El backend responde 409 si el curso tiene alumnos: hay que forzar el borrado.
      hasStudents: false,
    }
  },
  computed: {
    isConfirmed() {
      return this.confirmation.trim() === (this.course.title ?? '').trim()
    }
  },
  mounted() {
    this.$nextTick(() => this.$refs.confirmation?.focus())
  },
  methods: {
    close() {
      if (!this.deleting) {
        this.$emit('close')
      }
    },
    submit() {
      if (!this.isConfirmed) {
        return
      }

      this.deleting = true

      CourseService.destroy(this.course.uuid, this.hasStudents ? this.course.title : null)
          .then(() => {
            NotificationService.showNotification(this.$t('projects.course_deleted'), 'success')
            this.$emit('deleted', this.course)
          })
          .catch((error) => {
            if (error?.response?.status === HTTP_CONFLICT && !this.hasStudents) {
              this.hasStudents = true
              return
            }
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.deleting = false
          })
    }
  }
}
</script>
